// V4-FRONT-ANY-USAGE batch 5 2026-05-28
import { ExternalLink, FileText, Rss, Youtube, Globe, BookOpen, Info, Eye } from 'lucide-react';
import { AnimatedIconButton } from '../../../common/AnimatedIconButton';
import type { SourceReference } from '../../../../types/post';
import type { TranslationKeys } from '../../../../i18n/fr';

type SourceTypeKey = 'rss' | 'rss_feed' | 'youtube' | 'video' | 'website' | 'url' | 'raw_text' | 'text' | 'document' | 'pdf' | 'unknown';

interface SourceRowProps {
  source: SourceReference;
  t: TranslationKeys;
  onViewDetails?: (source: SourceReference) => void;
}

export const getSourceIcon = (type: string) => {
  switch ((type || '').toLowerCase()) {
    case 'rss':
    case 'rss_feed':
      return Rss;
    case 'youtube':
    case 'video':
      return Youtube;
    case 'website':
    case 'url':
      return Globe;
    case 'raw_text':
    case 'text':
      return FileText;
    case 'document':
    case 'pdf':
      return BookOpen;
    default:
      return Info;
  }
};

export const getRelevanceColor = (relevance: number): string => {
  if (relevance >= 0.8) return '#22c55e';
  if (relevance >= 0.5) return '#d97706';
  return '#94a3b8';
};

export const stripHtml = (text: string): string =>
  text.replace(/<[^>]*>?/gm, '').trim();

export function SourceRow({ source, t, onViewDetails }: SourceRowProps) {
  const IconComponent = getSourceIcon(source.type);
  const hasDetails = !!(
    source.facts?.length ||
    source.aiSummary ||
    source.postAngles?.length ||
    source.keywords?.length ||
    source.entityNames?.length
  );

  return (
    <div
      className="px-6 py-4 transition-colors cursor-default border-b last:border-0"
      style={{ backgroundColor: 'transparent', borderColor: 'var(--color-border)' }}
    >
      <div className="flex items-start gap-3">
        <div
          className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center mt-0.5"
          style={{ backgroundColor: 'var(--color-surface-hover)' }}
        >
          <IconComponent className="w-4 h-4" style={{ color: 'var(--color-text-secondary)' }} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3">
            <span
              className="text-sm font-medium line-clamp-2 leading-snug"
              style={{ color: 'var(--color-text-primary)' }}
              title={source.title}
            >
              {source.title}
            </span>

            {source.relevance !== undefined && (
              <span
                className="flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none flex-shrink-0"
                style={{
                  backgroundColor: getRelevanceColor(source.relevance),
                  color: '#fff',
                }}
                title={`${t.sources?.relevance || 'Pertinence'}: ${Math.round(source.relevance * 100)}% — ${t.sources?.relevanceTooltip || ''}`}
              >
                {Math.round(source.relevance * 100)}%
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-3 ml-11">
        <span
          className="text-xs font-medium px-2 py-1 rounded"
          style={{ backgroundColor: 'var(--color-surface-hover)', color: 'var(--color-text-secondary)' }}
        >
          {t.sourceTypes?.[source.type as SourceTypeKey] || source.type}
        </span>

        {source.url && (
          <AnimatedIconButton
            icon={ExternalLink}
            label={t.common?.viewSource || 'Voir la source'}
            variant="neutral"
            size="mini"
            expanded
            revealLabelOnHover={false}
            onClick={() => window.open(source.url, '_blank', 'noopener,noreferrer')}
            title={t.common?.viewSource || 'Voir la source'}
          />
        )}

        {hasDetails && onViewDetails && (
          <AnimatedIconButton
            icon={Eye}
            label={t.posts?.viewDetails || 'Voir détails'}
            variant="neutral"
            size="mini"
            revealLabelOnHover
            onClick={() => onViewDetails(source)}
            title={t.posts?.viewDetails || 'Voir détails'}
          />
        )}
      </div>

      {(() => {
        const cleanFacts = (source.facts || []).map(stripHtml).filter(f => f.length > 0);
        if (cleanFacts.length === 0) return null;
        return (
          <div className="mt-3 ml-11 flex flex-col gap-2">
            {cleanFacts.map((cleanFact, i) => (
              <div key={i} className="flex items-start gap-2.5">
                <div
                  className="flex items-center justify-center w-5 h-5 rounded-full text-[10px] font-bold shrink-0 mt-0.5"
                  style={{ backgroundColor: 'var(--color-primary-light)', color: 'var(--color-primary)' }}
                >
                  {i + 1}
                </div>
                <span
                  className="text-xs leading-relaxed"
                  style={{ color: 'var(--color-text-secondary)' }}
                >
                  {cleanFact}
                </span>
              </div>
            ))}
          </div>
        );
      })()}
    </div>
  );
}
